import {
  createCamelCasedClient,
  createTokenRefreshInterceptor,
  getCampaignParticipant,
  getOffer,
  getUser,
} from '@metapic-apps/shared'
import axios, { type AxiosError, type AxiosInstance } from 'axios'

import { config } from '@/lib/helpers'
import { observeAdBlockerFailure } from '@/lib/services/ad-blocker.service'
import { notifyUserBanned } from '@/lib/services/banned-user.service'
import { useAuthStore } from '@/stores/auth.stores'

const metapicApiAxios = axios.create({
  baseURL: config.metapicApiUrl,
})

if (import.meta.env.VITE_DEMO_MODE === 'true') {
  const { createDemoAdapter } = await import('@/demo/demo-adapter')
  metapicApiAxios.defaults.adapter = createDemoAdapter()
}

metapicApiAxios.interceptors.request.use(
  (request) => {
    const authStore = useAuthStore()
    request.headers.setAuthorization(authStore.authorization || '')
    request.headers.setContentType(request.isFormData ? 'multipart/form-data' : 'application/json')
    return request
  },
  (error: AxiosError) => Promise.reject(error),
)

metapicApiAxios.interceptors.response.use(
  (response) => response,
  (
    error: AxiosError<{
      code?: string
      message?: string
    }>,
  ) => {
    if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
      notifyUserBanned()
    }
    void observeAdBlockerFailure(error)
    return Promise.reject(error)
  },
)

createTokenRefreshInterceptor(metapicApiAxios, {
  refreshToken: () => useAuthStore().refreshToken(),
  onRefreshFailure: () => useAuthStore().handleRefreshFailure(),
})

const metapicApiClientFactory = (axiosInstance: AxiosInstance) => ({
  ...getUser(axiosInstance),
  ...getCampaignParticipant(axiosInstance),
  ...getOffer(axiosInstance),
})

export const metapicApiClient = createCamelCasedClient(metapicApiClientFactory, metapicApiAxios)
